<template>
    <Card>
        <CardHeader>
            <div class="flex items-center gap-2">
                <KeyRound class="size-4 text-muted-foreground" />
                <CardTitle class="text-sm font-medium text-primary">
                    {{ hasPassword ? 'Password' : 'Set password' }}
                </CardTitle>
            </div>
        </CardHeader>

        <CardContent>
            <div class="space-y-6">
                <div class="space-y-6">
                    <div v-if="hasPassword" class="w-full max-w-[373px]">
                        <Label for="current-password" class="text-sm font-medium text-foreground">Current password</Label>
                        <Input
                            id="current-password"
                            v-model="form.current_password"
                            type="password"
                            placeholder="Enter your current password"
                            class="mt-2"
                        />
                    </div>
                    <div class="w-full max-w-[373px]">
                        <Label for="new-password" class="text-sm font-medium text-foreground">
                            {{ hasPassword ? 'New password' : 'Password' }}
                        </Label>
                        <Input
                            id="new-password"
                            v-model="form.password"
                            type="password"
                            :placeholder="hasPassword ? 'Enter your new password' : 'Enter a password'"
                            class="mt-2"
                        />
                    </div>
                    <div class="w-full max-w-[373px]">
                        <Label for="confirm-password" class="text-sm font-medium text-foreground">Confirm new password</Label>
                        <Input
                            id="confirm-password"
                            v-model="form.password_confirmation"
                            type="password"
                            placeholder="Enter your new password"
                            class="mt-2"
                        />
                    </div>
                </div>

                <!-- SSO users without password need OTP confirmation -->
                <ConfirmsOtpV2
                    v-if="!hasPassword"
                    title="Verify your identity"
                    content="We sent a 6-digit code to your email address. Enter it below to set your new password."
                    @confirmed="updatePassword"
                >
                    <Button :disabled="loading || !form.password || !form.password_confirmation">
                        Update password
                    </Button>
                </ConfirmsOtpV2>

                <!-- Users with password submit directly -->
                <Button
                    v-else
                    :disabled="loading || !form.password || !form.password_confirmation"
                    @click="updatePassword"
                >
                    Update password
                </Button>
            </div>
        </CardContent>
    </Card>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import { router } from '@inertiajs/vue3';
import { route } from 'ziggy-js';
import { toast } from 'vue-sonner';
import { KeyRound } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import ConfirmsOtpV2 from './ConfirmsOtpV2.vue';

const props = defineProps<{
    hasPassword: boolean;
}>();

const loading = ref(false);

const form = ref({
    current_password: '',
    password: '',
    password_confirmation: '',
});

const updatePassword = () => {
    loading.value = true;

    router.put(
        route('settings.profile-settings.update'),
        form.value,
        {
            preserveScroll: true,
            onSuccess: () => {
                form.value = { current_password: '', password: '', password_confirmation: '' };
                toast.success('Password updated successfully!');
            },
            onError: (err) => {
                const msg = err?.updatePassword?.current_password
                    || err?.updatePassword?.password
                    || 'Failed to update password';
                toast.error(msg);
            },
            onFinish: () => {
                loading.value = false;
            },
        }
    );
};
</script>
